import React, { useEffect, useRef } from 'react';
import './MainLayout.scss';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // When embedded as the Analytics tab's iframe on BoardDetail.aspx (magicbullet),
    // report our real content height to the parent frame so it can grow the iframe to
    // fit and let the outer page scroll, instead of clipping us to a fixed height and
    // scrolling internally. No-op when not framed. We're cross-origin from magicbullet,
    // so postMessage is the only channel available - the parent can't just read
    // scrollHeight directly (see tab-iframe-resize.js there).
    if (window.self === window.top || !contentRef.current) {
      return;
    }

    const postHeight = (): void => {
      // Observe/measure .main-content, not document.body: html/body are both
      // height:100% (fills the viewport rather than growing with content), so once
      // content overflows, body's own box height stays pinned at 100% and a
      // ResizeObserver on it never fires again as async chart/report data loads in -
      // only one, too-small height would ever get posted. .main-content has no height
      // set (auto), so it reports its real, unclipped height and fires correctly on
      // every subsequent change.
      window.parent.postMessage(
        { type: 'boards-app-resize', height: contentRef.current!.scrollHeight },
        '*'
      );
    };

    const observer = new ResizeObserver(postHeight);
    observer.observe(contentRef.current);
    postHeight();

    return () => observer.disconnect();
  }, []);

  return (
    <div className="main-layout">
      <div className="main-content" ref={contentRef}>
        {children}
      </div>
    </div>
  );
};

export default MainLayout;
